import { coerceLedgerMoneyFields } from '@/components/ledgerMoney'
import api from './api'
import type {
  WarehouseBillingLineItem,
  WarehouseBrightSitesImportResponse,
  WarehouseCoverageResponse,
  WarehouseDraftResponse,
  WarehouseInventoryLayerItem,
  WarehouseInventoryMovementItem,
  WarehouseInvoiceSummary,
  WarehouseMonthAdjustmentItem,
  WarehouseProcessMonthResponse,
  WarehouseManualInvoiceMatchResponse,
  WarehouseClearMonthResponse,
  WarehouseDeleteMovementResponse,
  WarehouseRetryNoStockResponse,
  WarehouseCreateMonthDraftsResponse,
  WarehouseJeReadinessResponse,
  WarehouseDraftListResponse,
  WarehouseDraftActionResponse,
  WarehouseDeleteQboResponse,
  WarehouseReconcileIgnoreCreate,
  WarehouseReconcileIgnoreItem,
  WarehouseReconcileNoteUpsert,
  WarehouseHaloBillSyncResponse,
  WarehouseSkuCrosswalkItem,
  WarehouseSkuCrosswalkResponse,
  WarehouseInventoryManualEntriesResponse,
  WarehouseInventoryManualEntryItem,
  WarehouseUploadResponse,
  BrightSitesInventorySyncResponse,
  BrightSitesInventorySyncApplyRequest,
  BrightSitesInventorySyncApplyResponse,
  WarehouseLedgerCatalogAsOfResponse,
  WarehouseLedgerFlagIgnoreRequest,
  WarehouseLedgerImportBsLogsRequest,
  WarehouseLedgerImportBsLogsResponse,
  WarehouseLedgerListResponse,
  WarehouseLedgerTransactionItem,
  WarehouseLedgerTransactionPatch,
  WarehouseShippingAllocateResponse,
  WarehouseProductInvoiceLineItem,
  WarehouseProductInvoiceLinePatchRequest,
  WarehouseProductLineMatchResponse,
  WarehouseBillingAwarenessResponse,
  WarehouseBillingLineLinkResponse,
  WarehouseNavStatusResponse,
  WarehouseProductLineLinkResponse,
  WarehouseVendorItem,
  WarehouseVendorListResponse,
} from '@/services/warehouseTypes'

export type {
  WarehouseBillingLineItem,
  WarehouseBkMatchItem,
  WarehouseBrightSitesImportResponse,
  WarehouseCoverageIssue,
  WarehouseCoverageResponse,
  WarehouseDraftLine,
  WarehouseDraftResponse,
  WarehouseInventoryLayerItem,
  WarehouseInventoryMovementItem,
  WarehouseInvoiceSummary,
  WarehouseMonthAdjustmentItem,
  WarehouseMonthCoverage,
  WarehouseProcessMonthResponse,
  WarehouseManualInvoiceMatchResponse,
  WarehouseClearMonthResponse,
  WarehouseDeleteMovementResponse,
  WarehouseRetryNoStockResponse,
  WarehouseCreateMonthDraftsResponse,
  WarehouseQboBillMatch,
  WarehouseReconcileIgnoreCreate,
  WarehouseReconcileIgnoreItem,
  WarehouseReconcileNoteUpsert,
  WarehouseHaloBillSyncResponse,
  WarehouseSkuCrosswalkItem,
  WarehouseSkuCrosswalkResponse,
  WarehouseInventoryManualEntriesResponse,
  WarehouseInventoryManualEntryItem,
  WarehouseUploadResponse,
  BrightSitesCatalogProposal,
  BrightSitesProgressionProposal,
  BrightSitesInventorySyncResponse,
  BrightSitesInventorySyncApplyRequest,
  BrightSitesInventorySyncApplyResponse,
  WarehouseLedgerCatalogAsOfResponse,
  WarehouseLedgerImportBsLogsRequest,
  WarehouseLedgerFlagIgnoreRequest,
  WarehouseLedgerImportBsLogsResponse,
  WarehouseLedgerKind,
  WarehouseLedgerListResponse,
  WarehouseLedgerTransactionItem,
  WarehouseLedgerTransactionPatch,
  WarehouseShippingAllocateResponse,
  WarehouseProductInvoiceLineItem,
  WarehouseProductInvoiceLinePatchRequest,
  WarehouseProductLineMatchResponse,
  WarehouseBillingAwarenessResponse,
  WarehouseBillingLineLinkResponse,
  WarehouseNavStatusResponse,
  WarehouseProductLineLinkResponse,
  WarehouseVendorItem,
  WarehouseVendorListResponse,
} from '@/services/warehouseTypes'

const multipartHeaders = { headers: { 'Content-Type': 'multipart/form-data' } }

export async function listWarehouseVendors(): Promise<WarehouseVendorItem[]> {
  const { data } = await api.get<WarehouseVendorListResponse>('/warehouse/vendors')
  return data.vendors
}

export async function createWarehouseVendor(payload: {
  name: string
  qbo_vendor_id: string
}): Promise<WarehouseVendorItem> {
  const { data } = await api.post<WarehouseVendorItem>('/warehouse/vendors', payload)
  return data
}

export async function updateWarehouseVendor(
  vendorId: number,
  payload: { name?: string; qbo_vendor_id?: string }
): Promise<WarehouseVendorItem> {
  const { data } = await api.patch<WarehouseVendorItem>(`/warehouse/vendors/${vendorId}`, payload)
  return data
}

export async function deleteWarehouseVendor(vendorId: number): Promise<void> {
  await api.delete(`/warehouse/vendors/${vendorId}`)
}

export async function uploadWarehouseInvoice(
  file: File,
  vendorId: number
): Promise<WarehouseUploadResponse> {
  const form = new FormData()
  form.append('file', file)
  form.append('vendor_id', String(vendorId))
  const { data } = await api.post<WarehouseUploadResponse>(
    '/warehouse/upload/invoice',
    form,
    multipartHeaders
  )
  return data
}

export async function uploadWarehouseBilling(
  file: File,
  options?: { legacy?: boolean; reportMonth?: string }
): Promise<WarehouseUploadResponse> {
  const form = new FormData()
  form.append('file', file)
  form.append('legacy', String(options?.legacy ?? false))
  if (options?.reportMonth) {
    form.append('report_month', options.reportMonth)
  }
  const { data } = await api.post<WarehouseUploadResponse>(
    '/warehouse/upload/billing',
    form,
    multipartHeaders
  )
  return data
}

export async function uploadWarehouseWhInvoice(
  reportMonth: string,
  file: File
): Promise<WarehouseUploadResponse> {
  const form = new FormData()
  form.append('report_month', reportMonth)
  form.append('file', file)
  const { data } = await api.post<WarehouseUploadResponse>(
    '/warehouse/upload/wh-invoice',
    form,
    multipartHeaders
  )
  return data
}

export async function importBrightSitesOrders(): Promise<WarehouseBrightSitesImportResponse> {
  const { data } = await api.post<WarehouseBrightSitesImportResponse>('/warehouse/import/brightsites')
  return data
}

export async function syncBrightSitesInventory(): Promise<BrightSitesInventorySyncResponse> {
  const { data } = await api.post<BrightSitesInventorySyncResponse>(
    '/warehouse/sync/brightsites-inventory'
  )
  return data
}

export async function applyBrightSitesInventorySync(
  payload: BrightSitesInventorySyncApplyRequest
): Promise<BrightSitesInventorySyncApplyResponse> {
  const { data } = await api.post<BrightSitesInventorySyncApplyResponse>(
    '/warehouse/sync/brightsites-inventory/apply',
    payload
  )
  return data
}

export async function getWarehouseCoverage(): Promise<WarehouseCoverageResponse> {
  const { data } = await api.get<WarehouseCoverageResponse>('/warehouse/coverage')
  return data
}

export async function listWarehouseInvoices(): Promise<WarehouseInvoiceSummary[]> {
  const { data } = await api.get<WarehouseInvoiceSummary[]>('/warehouse/invoices')
  return data
}

export async function listWarehouseBillingLines(
  reportMonth?: string
): Promise<WarehouseBillingLineItem[]> {
  const { data } = await api.get<WarehouseBillingLineItem[]>('/warehouse/billing-lines', {
    params: reportMonth ? { report_month: reportMonth } : undefined,
  })
  return data
}

export async function deleteWarehouseInvoice(invoiceId: number): Promise<void> {
  await api.delete(`/warehouse/invoices/${invoiceId}`)
}

export type WarehouseInvoiceUpdatePayload = {
  invoice_number?: string
  invoice_date?: string
  invoice_type?: string
  vendor_id?: number
  total?: number
  storage_fee_total?: number
  admin_fee_total?: number
  pick_fee_total?: number
  receiving_fee_total?: number
  freight?: number
}

export async function updateWarehouseInvoice(
  invoiceId: number,
  payload: WarehouseInvoiceUpdatePayload
): Promise<WarehouseInvoiceSummary> {
  const { data } = await api.patch<WarehouseInvoiceSummary>(
    `/warehouse/invoices/${invoiceId}`,
    payload
  )
  return data
}

export async function deleteWarehouseBillingMonth(reportMonth: string): Promise<void> {
  await api.delete(`/warehouse/billing/${reportMonth}`)
}

export async function createWarehouseAdjustment(payload: {
  report_month: string
  adjustment_type: string
  amount: number
  note?: string
}): Promise<WarehouseMonthAdjustmentItem> {
  const { data } = await api.post<WarehouseMonthAdjustmentItem>('/warehouse/adjustments', payload)
  return data
}

export async function deleteWarehouseAdjustment(adjustmentId: number): Promise<void> {
  await api.delete(`/warehouse/adjustments/${adjustmentId}`)
}

export async function listWarehouseAdjustments(): Promise<WarehouseMonthAdjustmentItem[]> {
  const { data } = await api.get<WarehouseMonthAdjustmentItem[]>('/warehouse/adjustments')
  return data
}

export async function createWarehouseReconcileIgnore(
  payload: WarehouseReconcileIgnoreCreate
): Promise<WarehouseReconcileIgnoreItem> {
  const { data } = await api.post<WarehouseReconcileIgnoreItem>(
    '/warehouse/reconcile-ignores',
    payload
  )
  return data
}

export async function deleteWarehouseReconcileIgnore(ignoreId: number): Promise<void> {
  await api.delete(`/warehouse/reconcile-ignores/${ignoreId}`)
}

export async function upsertWarehouseReconcileNote(
  payload: WarehouseReconcileNoteUpsert
): Promise<void> {
  await api.post('/warehouse/reconcile-notes', payload)
}

export async function listWarehouseLayers(): Promise<WarehouseInventoryLayerItem[]> {
  const { data } = await api.get<WarehouseInventoryLayerItem[]>('/warehouse/layers')
  return data
}

export async function listWarehouseMovements(): Promise<WarehouseInventoryMovementItem[]> {
  const { data } = await api.get<WarehouseInventoryMovementItem[]>('/warehouse/movements')
  return data
}

export async function createBkInventoryDrafts(options?: {
  overwrite_posted?: boolean
  report_month?: string
}): Promise<WarehouseDraftResponse[]> {
  const body =
    options?.overwrite_posted || options?.report_month
      ? {
          ...(options.overwrite_posted ? { overwrite_posted: true } : {}),
          ...(options.report_month ? { report_month: options.report_month } : {}),
        }
      : undefined
  const { data } = await api.post<WarehouseDraftResponse[]>(
    '/warehouse/draft/bk-inventory',
    body
  )
  return data
}

export async function processWarehouseMonth(payload: {
  report_month: string
}): Promise<WarehouseProcessMonthResponse> {
  const { data } = await api.post<WarehouseProcessMonthResponse>('/warehouse/process-month', payload)
  return data
}

export async function matchManualEntriesToInvoices(): Promise<WarehouseManualInvoiceMatchResponse> {
  const { data } = await api.post<WarehouseManualInvoiceMatchResponse>(
    '/warehouse/match-manual-to-invoices'
  )
  return data
}

export async function clearWarehouseMonth(payload: {
  report_month: string
}): Promise<WarehouseClearMonthResponse> {
  const { data } = await api.post<WarehouseClearMonthResponse>('/warehouse/clear-month', payload)
  return data
}

export async function deleteWarehouseMovement(
  moveKey: string
): Promise<WarehouseDeleteMovementResponse> {
  const { data } = await api.post<WarehouseDeleteMovementResponse>(
    '/warehouse/movements/delete',
    { move_key: moveKey }
  )
  return data
}

export async function retryNoStockMovements(
  skus?: string[]
): Promise<WarehouseRetryNoStockResponse> {
  const { data } = await api.post<WarehouseRetryNoStockResponse>(
    '/warehouse/movements/retry-no-stock',
    { skus: skus ?? null }
  )
  return data
}

export async function createWarehouseMonthDrafts(payload: {
  report_month: string
  storage_fee: number
  admin_fee: number
  overwrite_posted?: boolean
}): Promise<WarehouseCreateMonthDraftsResponse> {
  const { data } = await api.post<WarehouseCreateMonthDraftsResponse>(
    '/warehouse/draft/month-journal-entries',
    payload
  )
  return data
}

export async function getWarehouseMonthJeReadiness(
  reportMonth: string
): Promise<WarehouseJeReadinessResponse> {
  const { data } = await api.get<WarehouseJeReadinessResponse>(
    '/warehouse/draft/month-readiness',
    { params: { report_month: reportMonth } }
  )
  return data
}

export async function listWarehouseDrafts(): Promise<WarehouseDraftListResponse> {
  const { data } = await api.get<WarehouseDraftListResponse>('/warehouse/drafts')
  return data
}

export async function regenerateWarehouseDraft(
  draftId: number
): Promise<WarehouseDraftActionResponse> {
  const { data } = await api.post<WarehouseDraftActionResponse>(
    `/warehouse/drafts/${draftId}/regenerate`
  )
  return data
}

export async function pushWarehouseDraftQboUpdate(
  draftId: number,
  options?: { regenerate_if_dirty?: boolean; force_regenerate?: boolean }
): Promise<WarehouseDraftActionResponse> {
  const { data } = await api.post<WarehouseDraftActionResponse>(
    `/warehouse/drafts/${draftId}/push-qbo-update`,
    {
      regenerate_if_dirty: options?.regenerate_if_dirty ?? true,
      force_regenerate: options?.force_regenerate ?? false,
    }
  )
  return data
}

export async function deleteWarehouseDraftFromQbo(
  draftId: number
): Promise<WarehouseDeleteQboResponse> {
  const { data } = await api.post<WarehouseDeleteQboResponse>(
    `/warehouse/drafts/${draftId}/delete-qbo`
  )
  return data
}

export async function syncWarehouseHaloBills(): Promise<WarehouseHaloBillSyncResponse> {
  const { data } = await api.post<WarehouseHaloBillSyncResponse>('/warehouse/sync/halo-bills')
  return data
}

export async function syncWarehouseQboBills(): Promise<WarehouseHaloBillSyncResponse> {
  const { data } = await api.post<WarehouseHaloBillSyncResponse>('/warehouse/sync/qbo-bills')
  return data
}

export async function getSkuCrosswalk(): Promise<WarehouseSkuCrosswalkResponse> {
  const { data } = await api.get<WarehouseSkuCrosswalkResponse>('/warehouse/sku-crosswalk')
  return data
}

export async function putSkuCrosswalk(
  rows: WarehouseSkuCrosswalkItem[]
): Promise<WarehouseSkuCrosswalkResponse> {
  const { data } = await api.put<WarehouseSkuCrosswalkResponse>('/warehouse/sku-crosswalk', {
    rows,
  })
  return data
}

export async function getInventoryManualEntries(): Promise<WarehouseInventoryManualEntriesResponse> {
  const { data } = await api.get<WarehouseInventoryManualEntriesResponse>(
    '/warehouse/inventory-manual-entries'
  )
  return data
}

export async function putInventoryManualEntries(
  rows: WarehouseInventoryManualEntryItem[]
): Promise<WarehouseInventoryManualEntriesResponse> {
  const { data } = await api.put<WarehouseInventoryManualEntriesResponse>(
    '/warehouse/inventory-manual-entries',
    { rows }
  )
  return data
}

export type WarehouseLedgerListParams = {
  date_from?: string
  date_to?: string
  kind?: string
  bk_sku?: string
  bs_changed_under_override?: boolean
  limit?: number
  offset?: number
}

export async function listWarehouseLedger(
  params?: WarehouseLedgerListParams
): Promise<WarehouseLedgerListResponse> {
  const { data } = await api.get<WarehouseLedgerListResponse>('/warehouse/ledger', {
    params,
  })
  return {
    ...data,
    items: data.items.map(coerceLedgerMoneyFields),
  }
}

export type WarehouseLedgerSheetExportRequest = {
  title?: string
  headers: string[]
  rows: string[][]
}

export type WarehouseLedgerSheetExportResponse = {
  spreadsheet_id: string
  url: string
  name: string
}

export async function exportWarehouseLedgerSheet(
  payload: WarehouseLedgerSheetExportRequest
): Promise<WarehouseLedgerSheetExportResponse> {
  const { data } = await api.post<WarehouseLedgerSheetExportResponse>(
    '/warehouse/ledger/export-sheet',
    payload
  )
  return data
}

export async function patchWarehouseLedgerTransaction(
  bkTxnId: string,
  payload: WarehouseLedgerTransactionPatch
): Promise<WarehouseLedgerTransactionItem> {
  const { data } = await api.patch<WarehouseLedgerTransactionItem>(
    `/warehouse/ledger/${bkTxnId}`,
    payload
  )
  return coerceLedgerMoneyFields(data)
}

export async function ignoreWarehouseLedgerFlag(
  bkTxnId: string,
  flagId: string
): Promise<WarehouseLedgerTransactionItem> {
  const payload: WarehouseLedgerFlagIgnoreRequest = { flag_id: flagId }
  const { data } = await api.post<WarehouseLedgerTransactionItem>(
    `/warehouse/ledger/${bkTxnId}/flag-ignores`,
    payload
  )
  return coerceLedgerMoneyFields(data)
}

export async function restoreWarehouseLedgerFlag(
  bkTxnId: string,
  flagId: string
): Promise<WarehouseLedgerTransactionItem> {
  const { data } = await api.delete<WarehouseLedgerTransactionItem>(
    `/warehouse/ledger/${bkTxnId}/flag-ignores/${encodeURIComponent(flagId)}`
  )
  return coerceLedgerMoneyFields(data)
}

export async function voidWarehouseLedgerTransaction(
  bkTxnId: string
): Promise<WarehouseLedgerTransactionItem> {
  const { data } = await api.post<WarehouseLedgerTransactionItem>(
    `/warehouse/ledger/${bkTxnId}/void`
  )
  return coerceLedgerMoneyFields(data)
}

export async function unvoidWarehouseLedgerTransaction(
  bkTxnId: string
): Promise<WarehouseLedgerTransactionItem> {
  const { data } = await api.post<WarehouseLedgerTransactionItem>(
    `/warehouse/ledger/${bkTxnId}/unvoid`
  )
  return coerceLedgerMoneyFields(data)
}

export async function getWarehouseLedgerCatalogAsOf(
  asOf: string
): Promise<WarehouseLedgerCatalogAsOfResponse> {
  const { data } = await api.get<WarehouseLedgerCatalogAsOfResponse>(
    '/warehouse/ledger/catalog-as-of',
    { params: { as_of: asOf } }
  )
  return data
}

export async function importWarehouseLedgerBsLogs(
  payload?: WarehouseLedgerImportBsLogsRequest
): Promise<WarehouseLedgerImportBsLogsResponse> {
  const { data } = await api.post<WarehouseLedgerImportBsLogsResponse>(
    '/warehouse/ledger/import-bs-logs',
    payload ?? {}
  )
  return data
}

export async function allocateWarehouseWhInvoiceShipping(
  invoiceId: number
): Promise<WarehouseShippingAllocateResponse> {
  const { data } = await api.post<WarehouseShippingAllocateResponse>(
    `/warehouse/uploads/wh-invoices/${invoiceId}/allocate-shipping`
  )
  return data
}

export async function createWarehouseProductInvoiceLine(
  invoiceId: number
): Promise<WarehouseProductInvoiceLineItem> {
  const { data } = await api.post<WarehouseProductInvoiceLineItem>(
    `/warehouse/uploads/product-invoices/${invoiceId}/lines`
  )
  return data
}

export async function patchWarehouseProductInvoiceLine(
  lineId: number,
  payload: WarehouseProductInvoiceLinePatchRequest
): Promise<WarehouseProductInvoiceLineItem> {
  const { data } = await api.patch<WarehouseProductInvoiceLineItem>(
    `/warehouse/uploads/product-invoice-lines/${lineId}`,
    payload
  )
  return data
}

export async function deleteWarehouseProductInvoiceLine(lineId: number): Promise<void> {
  await api.delete(`/warehouse/uploads/product-invoice-lines/${lineId}`)
}

export async function listWarehouseProductInvoiceLines(
  invoiceId: number
): Promise<WarehouseProductInvoiceLineItem[]> {
  const { data } = await api.get<WarehouseProductInvoiceLineItem[]>(
    `/warehouse/uploads/product-invoices/${invoiceId}/lines`
  )
  return data
}

export async function recommendWarehouseProductLineMatches(
  lineId: number
): Promise<WarehouseProductLineMatchResponse> {
  const { data } = await api.get<WarehouseProductLineMatchResponse[]>(
    `/warehouse/uploads/product-lines/${lineId}/recommend-matches`
  )
  return data[0] ?? { line_id: lineId, matches: [] }
}

export async function ignoreWarehouseProductLine(
  lineId: number,
  ignored: boolean,
  ignoreReason?: string | null
): Promise<WarehouseProductInvoiceLineItem> {
  const { data } = await api.post<WarehouseProductInvoiceLineItem>(
    `/warehouse/uploads/product-lines/${lineId}/ignore`,
    { ignored, ignore_reason: ignoreReason ?? undefined }
  )
  return data
}

export async function unlinkWarehouseProductLine(
  lineId: number
): Promise<WarehouseProductInvoiceLineItem> {
  const { data } = await api.post<WarehouseProductInvoiceLineItem>(
    `/warehouse/uploads/product-lines/${lineId}/unlink`
  )
  return data
}

export async function unlinkWarehouseProductLineTxn(
  lineId: number,
  bkTxnId: string
): Promise<WarehouseProductInvoiceLineItem> {
  const { data } = await api.post<WarehouseProductInvoiceLineItem>(
    `/warehouse/uploads/product-lines/${lineId}/unlink-txn`,
    { bk_txn_id: bkTxnId }
  )
  return data
}

export async function setWarehouseProductLineQtyBasis(
  lineId: number,
  qtyBasis: 'units' | 'cases' | 'auto'
): Promise<WarehouseProductInvoiceLineItem> {
  const { data } = await api.post<WarehouseProductInvoiceLineItem>(
    `/warehouse/uploads/product-lines/${lineId}/qty-basis`,
    { qty_basis: qtyBasis }
  )
  return data
}

export async function setWarehouseProductInvoiceLineSku(
  lineId: number,
  skuId: string
): Promise<WarehouseProductInvoiceLineItem> {
  const { data } = await api.post<WarehouseProductInvoiceLineItem>(
    `/warehouse/uploads/product-invoice-lines/${lineId}/sku`,
    { sku_id: skuId }
  )
  return data
}

export async function linkWarehouseProductLine(
  lineId: number,
  bkTxnId: string
): Promise<WarehouseProductLineLinkResponse> {
  const { data } = await api.post<WarehouseProductLineLinkResponse>(
    `/warehouse/uploads/product-lines/${lineId}/link`,
    { bk_txn_id: bkTxnId }
  )
  return data
}

export async function linkWarehouseBillingLine(
  lineId: number,
  bkTxnId: string
): Promise<WarehouseBillingLineLinkResponse> {
  const { data } = await api.post<WarehouseBillingLineLinkResponse>(
    `/warehouse/uploads/billing-lines/${lineId}/link`,
    { bk_txn_id: bkTxnId }
  )
  return data
}

export async function getWarehouseBillingAwareness(
  reportMonth: string
): Promise<WarehouseBillingAwarenessResponse> {
  const { data } = await api.get<WarehouseBillingAwarenessResponse>(
    `/warehouse/uploads/billing/${reportMonth}/awareness`
  )
  return data
}

export async function getWarehouseNavStatus(): Promise<WarehouseNavStatusResponse> {
  const { data } = await api.get<WarehouseNavStatusResponse>('/warehouse/nav-status')
  return data
}
